class MemoryLeakDetector {
    constructor() {
        this.enabled = import.meta.env.DEV
        this.listeners = new Map()
        this.intervals = new Set()
        this.timeouts = new Set()
        this.observers = new Set()
        this.echoChannels = new Set()

        if (this.enabled) {
            this.patchGlobalAPIs()
            this.startMonitoring()
        }
    }

    patchGlobalAPIs() {
        const originalAddEventListener = EventTarget.prototype.addEventListener
        const originalRemoveEventListener = EventTarget.prototype.removeEventListener
        const originalSetInterval = window.setInterval
        const originalClearInterval = window.clearInterval
        const originalSetTimeout = window.setTimeout
        const originalClearTimeout = window.clearTimeout

        EventTarget.prototype.addEventListener = function (type, listener, options) {
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                const key = `${this.constructor.name}-${type}`
                if (!detector.listeners.has(key)) {
                    detector.listeners.set(key, new Set())
                }
                detector.listeners.get(key).add(listener)
            }
            return originalAddEventListener.call(this, type, listener, options)
        }

        EventTarget.prototype.removeEventListener = function (type, listener, options) {
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                const key = `${this.constructor.name}-${type}`
                const listenersSet = detector.listeners.get(key)
                if (listenersSet) {
                    listenersSet.delete(listener)
                    if (listenersSet.size === 0) {
                        detector.listeners.delete(key)
                    }
                }
            }
            return originalRemoveEventListener.call(this, type, listener, options)
        }

        window.setInterval = function (handler, timeout, ...args) {
            const id = originalSetInterval.call(window, handler, timeout, ...args)
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                detector.intervals.add(id)
            }
            return id
        }

        window.clearInterval = function (id) {
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                detector.intervals.delete(id)
            }
            return originalClearInterval.call(window, id)
        }

        window.setTimeout = function (handler, timeout, ...args) {
            const id = originalSetTimeout.call(window, handler, timeout, ...args)
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                detector.timeouts.add(id)
                originalSetTimeout.call(
                    window,
                    () => {
                        if (detector && detector.enabled) {
                            detector.timeouts.delete(id)
                        }
                    },
                    timeout + 100,
                )
            }
            return id
        }

        window.clearTimeout = function (id) {
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                detector.timeouts.delete(id)
            }
            return originalClearTimeout.call(window, id)
        }

        const originalObserve = ResizeObserver.prototype.observe
        const originalDisconnect = ResizeObserver.prototype.disconnect

        ResizeObserver.prototype.observe = function (...args) {
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                detector.observers.add(this)
            }
            return originalObserve.call(this, ...args)
        }

        ResizeObserver.prototype.disconnect = function () {
            const detector = window.__memoryLeakDetector
            if (detector && detector.enabled) {
                detector.observers.delete(this)
            }
            return originalDisconnect.call(this)
        }
    }

    startMonitoring() {
        if (!this.enabled) return

        setInterval(() => {
            this.checkForLeaks()
        }, 30000)
    }

    checkForLeaks() {
        const issues = []

        if (this.listeners.size > 50) {
            issues.push(`⚠️ High number of event listeners: ${this.listeners.size} types`)
            console.warn(
                'Event listeners by type:',
                Array.from(this.listeners.entries()).map(([key, set]) => ({
                    type: key,
                    count: set.size,
                })),
            )
        }

        if (this.intervals.size > 10) {
            issues.push(`⚠️ High number of active intervals: ${this.intervals.size}`)
        }

        if (this.timeouts.size > 50) {
            issues.push(`⚠️ High number of active timeouts: ${this.timeouts.size}`)
        }

        if (this.observers.size > 20) {
            issues.push(`⚠️ High number of active observers: ${this.observers.size}`)
        }

        if (issues.length > 0) {
            console.group('🔍 Memory Leak Detection Report')
            issues.forEach((issue) => console.warn(issue))
            console.groupEnd()
        }
    }

    getReport() {
        return {
            listeners: Array.from(this.listeners.entries()).map(([key, set]) => ({
                type: key,
                count: set.size,
            })),
            intervals: this.intervals.size,
            timeouts: this.timeouts.size,
            observers: this.observers.size,
            echoChannels: this.echoChannels.size,
        }
    }

    logReport() {
        const report = this.getReport()
        console.group('📊 Memory Leak Detector Report')
        console.log('Event Listeners:', report.listeners)
        console.log('Active Intervals:', report.intervals)
        console.log('Active Timeouts:', report.timeouts)
        console.log('Active Observers:', report.observers)
        console.log('Echo Channels:', report.echoChannels)
        console.groupEnd()
    }

    trackEchoChannel(channelName) {
        if (this.enabled) {
            this.echoChannels.add(channelName)
        }
    }

    untrackEchoChannel(channelName) {
        if (this.enabled) {
            this.echoChannels.delete(channelName)
        }
    }
}

const detector = new MemoryLeakDetector()

if (detector.enabled) {
    window.__memoryLeakDetector = detector

    window.checkMemoryLeaks = () => {
        detector.logReport()
    }

    console.log(
        '🔍 Memory Leak Detector enabled. Call window.checkMemoryLeaks() to see current status.',
    )
}

export default detector
